import type { Squad } from '../../../types';

export type TeacherLogisticsKey =
  | 'wifiUsername'
  | 'wifiPassword'
  | 'arrivalProcedure'
  | 'dismissalProcedure'
  | 'lateArrivalProcedure'
  | 'bathroomRules'
  | 'hallwayRules'
  | 'parkingInstructions'
  | 'classroomName'
  | 'hasProjectorOrTv'
  | 'projectorConnectionInstructions';

export const LOCATION_TEACHING_LOGISTICS_FIELDS = [
  'wifiUsername',
  'wifiPassword',
  'arrivalProcedure',
  'dismissalProcedure',
  'lateArrivalProcedure',
  'bathroomRules',
  'hallwayRules',
  'parkingInstructions',
] as const;

export const SQUAD_TEACHING_LOGISTICS_FIELDS = [
  'classroomName',
  'hasProjectorOrTv',
  'projectorConnectionInstructions',
  'additionalNotes',
] as const;

export type MissingTeacherLogistic = {
  key: TeacherLogisticsKey;
  label: string;
};

function isBlank(v: unknown): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim() === '';
  return false;
}

function normalizeBoolean(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true') return true;
    if (s === 'false') return false;
  }
  return null;
}

/**
 * Teacher-editable logistics completeness
 *
 * Notes:
 * - We intentionally treat "Projector/TV available" as required to be explicitly answered (Yes/No),
 *   so "Unknown" counts as incomplete.
 * - "How to connect to projector" is only required when "Projector/TV available" is Yes.
 */
export function getMissingTeacherLogistics(squad: Partial<Squad> | null | undefined): MissingTeacherLogistic[] {
  const s: any = squad || {};
  const unknown: any = s?.teacherLogisticsUnknown || {};
  const missing: MissingTeacherLogistic[] = [];

  const add = (key: TeacherLogisticsKey, label: string) => {
    missing.push({ key, label });
  };

  const isUnknown = (key: TeacherLogisticsKey) => unknown?.[key] === true;

  if (!isUnknown('wifiUsername') && isBlank(s.wifiUsername)) add('wifiUsername', 'WiFi username');
  if (!isUnknown('wifiPassword') && isBlank(s.wifiPassword)) add('wifiPassword', 'WiFi password');
  if (!isUnknown('arrivalProcedure') && isBlank(s.arrivalProcedure)) add('arrivalProcedure', 'Arrival procedure');
  if (!isUnknown('dismissalProcedure') && isBlank(s.dismissalProcedure)) add('dismissalProcedure', 'Dismissal procedure');
  if (!isUnknown('lateArrivalProcedure') && isBlank(s.lateArrivalProcedure)) add('lateArrivalProcedure', 'Late arrival rules');
  if (!isUnknown('bathroomRules') && isBlank(s.bathroomRules)) add('bathroomRules', 'Bathroom rules');
  if (!isUnknown('hallwayRules') && isBlank(s.hallwayRules)) add('hallwayRules', 'Hallway rules');
  if (!isUnknown('parkingInstructions') && isBlank(s.parkingInstructions)) add('parkingInstructions', 'Parking instructions');
  if (!isUnknown('classroomName') && isBlank(s.classroomName)) add('classroomName', 'Classroom name');

  const projector = normalizeBoolean(s.hasProjectorOrTv);
  if (!isUnknown('hasProjectorOrTv') && projector === null) {
    add('hasProjectorOrTv', 'Projector / TV available');
  } else if (projector === true) {
    if (!isUnknown('projectorConnectionInstructions') && isBlank(s.projectorConnectionInstructions)) {
      add('projectorConnectionInstructions', 'How to connect to projector');
    }
  }

  return missing;
}

export function hasIncompleteTeacherLogistics(squad: Partial<Squad> | null | undefined): boolean {
  return getMissingTeacherLogistics(squad).length > 0;
}

